import data from './data/swrEtfCalibration.json';
import { getSWRSimpleTaxRate, createSWRSimpleTaxProfile } from './swrTaxModel';

export const SWR_ETF_CALIBRATION_VERSION = data.version;
export const SWR_ETF_DATASET_HASH = data.manifest.monthlyObservationsSha256;
export const SWR_MODEL_PORTFOLIOS = data.portfolios.map(({ monthlyReturns, sensitivity, statistics, ...preset }) => ({ ...preset,
  taxRatePct: getSWRSimpleTaxRate(preset.equityPct / 100) * 100, taxProfile: createSWRSimpleTaxProfile(preset.equityPct), cagrPct: statistics.cagr * 100 }));
const legacy = [
  { id: 'eq40-bond60', label: '40 / 60', equityPct: 40, bondPct: 60, expectedReturnPct: 6.8, annualVolatilityPct: 6.45, taxRatePct: 22 },
  { id: 'eq50-bond50', label: '50 / 50', equityPct: 50, bondPct: 50, expectedReturnPct: 7.15, annualVolatilityPct: 7.7, taxRatePct: 23 },
  { id: 'eq60-bond40', label: '60 / 40', equityPct: 60, bondPct: 40, expectedReturnPct: 7.45, annualVolatilityPct: 9, taxRatePct: 24 },
];

export const getSWRPortfolioById = id => [...SWR_MODEL_PORTFOLIOS, ...legacy].find(p => p.id === id) || null;

export function getVerifiedSWRPortfolio(params) {
  const id = params.portfolioPreset || params.modelPortfolio?.id;
  const known = data.portfolios.find(p => p.id === id);
  const calibration = params.modelPortfolio?.calibration;
  const metadataMatches = known && params.modelPortfolio?.id === known.id
    && params.modelPortfolio?.equityPct === known.equityPct && params.modelPortfolio?.bondPct === known.bondPct
    && Object.entries(known.calibration).every(([key, value]) => key === 'logStatistics'
      ? Object.entries(value).every(([stat, number]) => calibration?.logStatistics?.[stat] === number)
      : calibration?.[key] === value);
  if (!metadataMatches || !Number.isFinite(params.expectedReturn) || !Number.isFinite(params.annualVolatility)
    || calibration?.version !== data.version || calibration?.datasetSha256 !== SWR_ETF_DATASET_HASH
    || Math.abs(params.expectedReturn - known.expectedReturnPct / 100) > 1e-12
    || Math.abs(params.annualVolatility - known.annualVolatilityPct / 100) > 1e-12) return null;
  return known;
}

export const getSWRObservedLogStatistics = params => getVerifiedSWRPortfolio(params)?.calibration.logStatistics || null;

export function getSWRHistoricalReplay(params) {
  const portfolio = getVerifiedSWRPortfolio(params);
  if (!portfolio) return null;
  const observations = portfolio.monthlyReturns.filter(row => row.month >= '2020-01' && row.month <= '2022-12');
  return {
    id: 'etf-replay-2020-2022',
    type: 'monthly-replay',
    name: 'Replay ETF 2020–2022',
    shortDescription: 'Sequenza mensile osservata: pandemia e rialzo dei tassi, nel mix ETF selezionato.',
    detailedDescription: 'I primi 36 rendimenti mensili del portafoglio sono quelli del mix di ETF dal gennaio 2020 al dicembre 2022, in euro e con ribilanciamento annuale. Nei mesi successivi riprende il percorso Monte Carlo. Per piani più brevi si usa soltanto il tratto iniziale disponibile.',
    logic: 'Verifica il piano contro una sequenza effettivamente osservata nei fondi scelti. Inflazione e buffer mantengono le ipotesi configurate: è un replay del mercato, non una ricostruzione congiunta dell’economia storica.',
    assumptions: ['Fonte: NAV pubblici iShares e DWS, conversione USD/EUR con cambi BCE; gennaio 2020–dicembre 2022.',
      'Dividendi reinvestiti e costi dei fondi già compresi. Ribilanciamento annuale; fiscalità personale e di ribilanciamento non compresa nei rendimenti.',
      'Il replay riguarda il portafoglio investito; CPI e cash restano quelli del modello scelto.',
      'La scelta di questa finestra avversa non stima la frequenza futura di crisi.'],
    evidenceType: 'historical-market-replay',
    pathConvention: 'observed-monthly-etf-returns',
    source: { calibrationVersion: data.version, datasetSha256: SWR_ETF_DATASET_HASH,
      equityISIN: 'IE00B4L5Y983', bondISIN: 'LU0378818131', equityPct: portfolio.equityPct, periodStart: '2020-01', periodEnd: '2022-12' },
    monthlyReturns: observations.map(row => row.return),
  };
}
